import React from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Container, PillLink, Reveal, shot } from './primitives';

// Full class names so Tailwind keeps them (it can't see interpolated class names).
const CANVAS_CLASSES = {
  clients: 'lp-canvas-clients',
  projects: 'lp-canvas-projects',
  finance: 'lp-canvas-finance'
};

/**
 * Text + chip cluster on one side, a real product screen bleeding out of a
 * gradient canvas on the other. Copy comes from `landing.features.<id>`.
 */
export default function FeatureSection({ id, chips, canvas, image, overlay, className = '' }) {
  const { t, i18n } = useTranslation();
  const key = `landing.features.${id}`;
  const main = shot(image, i18n.language);
  const extra = overlay ? shot(overlay, i18n.language) : null;

  return (
    <section id={id} className={`lp-section scroll-mt-16 ${className}`} aria-labelledby={`${id}-title`}>
      <Container>
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-[72px]">
          <Reveal>
            <p className="lp-eyebrow">{t(`${key}.eyebrow`)}</p>
            <h2 id={`${id}-title`} className="lp-h2 mt-4 max-w-[620px]">
              {t(`${key}.title`)}
            </h2>
            <p className="lp-body mt-6 max-w-[480px]">
              <span className="font-medium text-ink">{t(`${key}.lead`)}</span>
              <br />
              {t(`${key}.description`)}
            </p>
            <ul className="mt-6 flex max-w-[560px] flex-wrap gap-2" aria-label={t(`${key}.chipsLabel`)}>
              {chips.map(({ id: chipId, icon }) => (
                <Chip key={chipId} icon={icon}>
                  {t(`${key}.chips.${chipId}`)}
                </Chip>
              ))}
            </ul>
            <div className="mt-8">
              <PillLink to="/register">{t('landing.cta.getStarted')}</PillLink>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className={`lp-canvas ${CANVAS_CLASSES[canvas]} aspect-[654/494] w-full`}>
              <img
                src={main.src}
                width={main.width}
                height={main.height}
                alt={t(`${key}.alt`)}
                loading="lazy"
                decoding="async"
                className="lp-shot absolute start-[9%] top-[12%] w-[120%]"
              />
              {extra && (
                <img
                  src={extra.src}
                  width={extra.width}
                  height={extra.height}
                  alt={t(`${key}.overlayAlt`)}
                  loading="lazy"
                  decoding="async"
                  className="lp-shot absolute end-[4%] top-[38%] hidden w-[38%] shadow-[0_2px_4px_rgba(0,0,0,0.08),0_28px_56px_-12px_rgba(0,0,0,0.45)] sm:block"
                />
              )}
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}
